/**
 * Mechanics — the skills the decision layer invokes. Each returns a `CarInput`
 * and nothing else: no state, no memory, no world mutation. All of the memory a
 * bot has lives in ./bot, so a mechanic can be called with a hand-built car
 * pose in a test and asserted directly.
 *
 * ===========================================================================
 * STEERING IS INVERTED THROUGH THE REAL TURN MODEL
 * ===========================================================================
 *
 * `driveToPoint` does not steer with a hand-tuned proportional gain on the
 * heading error. The car's yaw rate is an exactly known function of speed and
 * steer input — `steerYawRate` in ../physics/ground, which is the same function
 * the simulation uses — so the controller asks for a yaw rate and INVERTS that
 * function to get the input:
 *
 *     desired = angleError / TURN_TIME
 *     steer   = desired / steerYawRate(forwardSpeed, 1, 0)
 *
 * The denominator is `curvature(v) * v`, which collapses toward zero at low
 * speed; the clamp to +/-1 then produces full lock, which is correct — a
 * stationary car cannot turn however hard you push the stick. Using the real
 * curve instead of a gain means the same constant works at 300 uu/s and at
 * 2000 uu/s, where a fixed gain oscillates at one end and understeers at the
 * other.
 *
 * ===========================================================================
 * THE TWO WAYS A DRIVE-TO-POINT CONTROLLER FAILS, AND THE FIX FOR EACH
 * ===========================================================================
 *
 * 1. ORBITING. The target sits inside the car's minimum turn circle, the car
 *    holds full lock, and it circles forever at a fixed distance. Nothing about
 *    steering can fix this — the circle's radius is set by SPEED. Two defences:
 *
 *      - A corner-speed limit. `maxSpeedForRadius` inverts the curvature curve
 *        to find the fastest speed whose turn radius still fits the arc through
 *        the target, and the throttle controller obeys it. Slowing down shrinks
 *        the circle until the target is outside it.
 *      - When even the crawling turn radius will not fit (`tooTight`), the car
 *        reverses. That is what a player does, and it is the only manoeuvre
 *        that converges when the target is 150 uu off the nose at 90 degrees.
 *
 * 2. OVERSHOOT. The car arrives at speed and sails past. The arrival speed is
 *    capped at `sqrt(2 * ARRIVE_DECEL * distance)`, so the profile is a braking
 *    ramp rather than a stop command issued too late.
 *
 * Both are asserted in test/bot/mechanics.test.ts from a spread of start poses
 * including targets directly behind the car.
 *
 * ===========================================================================
 * DODGE SIGN CONVENTIONS
 * ===========================================================================
 *
 * ../physics/jump computes the dodge direction as `x = -input.pitch`,
 * `y = input.yaw + input.roll`. So a FORWARD flip is `pitch: -1`, and the flip
 * CANCEL — which needs the pitch input to share the sign of `dodgeDirection.x`
 * — is `pitch: +1`. Getting these backwards produces a backflip on kickoff,
 * which is why they are written out here rather than inferred at the call site.
 */

import { neutralInput, type CarInput, type CarState } from '../physics/car-state';
import {
  BALL_RADIUS,
  CAR_MAX_SPEED,
  CAR_SPAWN_REST_Z,
  DOUBLE_JUMP_IMPULSE,
  DT,
  GRAVITY,
  JUMP_HOLD_ACCELERATION,
  JUMP_IMPULSE,
  JUMP_MAX_HOLD_TIME,
  JUMP_MIN_HOLD_TIME,
  JUMP_PRE_MIN_ACCEL_SCALE,
  STICKY_FORCE_GROUND_ACCEL,
} from '../physics/constants';
import { OCTANE_CONFIG, type CarConfig } from '../physics/car-config';
import { steerCurvature, steerYawRate } from '../physics/ground';
import { wasJumpDown } from '../physics/jump';
import {
  clamp,
  dot,
  forward,
  length,
  mulVecT,
  normalize,
  sub,
  up as upOf,
  vec3,
  type Vec3,
} from '../physics/math';
import type { BallPrediction } from './prediction';
import { makeSample } from './prediction';

// ---------------------------------------------------------------------------
// Input hygiene
// ---------------------------------------------------------------------------

/** Clamp an axis into [-1, 1], mapping NaN and Infinity to 0. A controller that
 *  divides by a speed will produce a non-finite value eventually; the physics
 *  would propagate it into the car state and never recover. */
const axis = (v: number): number => (Number.isFinite(v) ? clamp(v, -1, 1) : 0);

/** Force every field of an input into its legal range, in place. */
export const sanitizeInput = (input: CarInput): CarInput => {
  input.throttle = axis(input.throttle);
  input.steer = axis(input.steer);
  input.pitch = axis(input.pitch);
  input.yaw = axis(input.yaw);
  input.roll = axis(input.roll);
  input.jump = input.jump === true;
  input.boost = input.boost === true;
  input.handbrake = input.handbrake === true;
  return input;
};

// ---------------------------------------------------------------------------
// Tuning — every one of these is a controller gain, not a physics constant
// ---------------------------------------------------------------------------

/** Seconds the steering controller is given to null the heading error. Shorter
 *  saturates the steer input and behaves like bang-bang; longer understeers
 *  into corners. */
const TURN_TIME = 0.28;

/** Speed error, in uu/s, that saturates the throttle. */
const SPEED_BAND = 140;

/** Deceleration assumed for the arrival ramp. Below the real brake authority
 *  (3500) so the ramp is conservative and the car does not have to brake at the
 *  limit to stop where it was asked to. */
const ARRIVE_DECEL = 1000;

/** Heading error, radians, inside which boost is allowed. Boosting through a
 *  turn wastes the tank and pushes the car wide. */
const BOOST_ALIGN = 0.28;

/** Speed deficit, uu/s, below which boost is not worth spending. */
const BOOST_SPEED_MARGIN = 60;

/** Heading error above which the handbrake helps rather than hurts. */
const SLIDE_ANGLE = 1.0;
/** Below this speed a powerslide just stops the car. */
const SLIDE_MIN_SPEED = 700;

/** Forward speed above which reversing costs more than turning around. */
const REVERSE_MAX_SPEED = 600;

/**
 * Distance beyond which reversing is never right, however badly the car is
 * facing. Backing up is capped at the same 1400 uu/s as driving, so for a
 * target on the far side of the field a 180-degree turn — which costs about one
 * turn circumference, under 4000 uu at speed — is always cheaper than reversing
 * the whole way. Without this cap the controller happily reverses across the
 * entire arena, which converges but takes twice as long and cannot boost.
 */
const REVERSE_MAX_DISTANCE = 2000;

/** The tightest circle the car can drive, at a crawl: 1 / 0.0069 = 144.9 uu. */
const MIN_TURN_RADIUS = 1 / steerCurvature(0);

/**
 * Contact offset from the ball centre to the car origin for a ground hit.
 *
 * Body-dependent: a Dominus is 9.92 uu longer than an Octane, so a bot driving
 * one and aiming at the Octane offset stops ~5 uu short of the ball on every
 * shot. Defaults to Octane so existing callers are unchanged.
 */
const hitOffsetFor = (config: CarConfig = OCTANE_CONFIG): number =>
  BALL_RADIUS + config.hitboxSize.x * 0.5;

// ---------------------------------------------------------------------------
// driveToPoint
// ---------------------------------------------------------------------------

export interface DriveOptions {
  /** Cap on the speed the car will carry, uu/s. */
  targetSpeed?: number;
  allowBoost?: boolean;
  allowPowerslide?: boolean;
  allowReverse?: boolean;
  /** Do not spend boost below this. */
  boostReserve?: number;
  /** Hard cap on |throttle|, from the difficulty tier. */
  throttleCap?: number;
  /** Stop on the point instead of driving through it. */
  stopAtTarget?: boolean;
  /** Distance at which the car counts as arrived, uu. */
  arriveTolerance?: number;
}

/**
 * Fastest speed at which the car's minimum turn radius still fits inside
 * `radius`. Bisection on `steerCurvature`, which is monotonically decreasing in
 * speed, so 20 iterations resolve to well under 1 uu/s.
 */
export const maxSpeedForRadius = (radius: number): number => {
  if (!Number.isFinite(radius) || radius <= 0) {
    return radius <= 0 ? 0 : CAR_MAX_SPEED;
  }
  if (steerCurvature(CAR_MAX_SPEED) * radius >= 1) return CAR_MAX_SPEED;
  if (steerCurvature(0) * radius < 1) return 0;

  let lo = 0;
  let hi = CAR_MAX_SPEED;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) * 0.5;
    if (steerCurvature(mid) * radius >= 1) lo = mid;
    else hi = mid;
  }
  return lo;
};

/** Drive the car toward a world point. The workhorse; every other ground
 *  mechanic ends up here with a different target. */
export const driveToPoint = (
  car: CarState,
  target: Vec3,
  options: DriveOptions = {}
): CarInput => {
  const out = neutralInput();
  const throttleCap = clamp(options.throttleCap ?? 1, 0, 1);
  const tolerance = options.arriveTolerance ?? 40;

  // Work in the car's own frame: local +x is forward, +y is left, so the
  // heading error is one atan2 and the "is it behind me" test is a sign.
  const local = mulVecT(car.orientation, sub(target, car.position));
  const planar = Math.hypot(local.x, local.y);
  const angle = Math.atan2(local.y, local.x);
  const speed = length(car.velocity);
  const vForward = dot(car.velocity, forward(car.orientation));

  if (planar <= tolerance) {
    // Arrived. Either kill the remaining speed, or — and this is the case that
    // matters for a shot — keep driving THROUGH the point. Coasting here parks
    // the car a tolerance-width short of the ball and it never makes contact,
    // which looks exactly like a bot that has decided not to bother.
    out.throttle = options.stopAtTarget
      ? axis(-vForward / SPEED_BAND) * throttleCap
      : throttleCap;
    return sanitizeInput(out);
  }

  // Radius of the circle through the car, tangent to its heading, that passes
  // through the target. The turn is impossible below `MIN_TURN_RADIUS`.
  const sinA = Math.abs(Math.sin(angle));
  const requiredRadius = sinA > 1e-3 ? planar / (2 * sinA) : Number.POSITIVE_INFINITY;
  const tooTight = requiredRadius < MIN_TURN_RADIUS * 1.05;

  const wantReverse =
    (options.allowReverse ?? true) &&
    planar < REVERSE_MAX_DISTANCE &&
    (local.x < 0 || tooTight) &&
    vForward < REVERSE_MAX_SPEED;

  if (wantReverse) {
    out.throttle = -throttleCap;
    // Steering authority follows the direction the car is actually rolling: the
    // yaw rate is `curvature * forwardSpeed * steer`, so it flips sign with the
    // speed. While still rolling forward (i.e. braking) steer toward the target;
    // once moving backwards, steer away from it to swing the nose around.
    const towardTarget = angle > 0 ? 1 : -1;
    out.steer = vForward < 0 ? -towardTarget : towardTarget;
    return sanitizeInput(out);
  }

  // --- steering: invert the real yaw-rate model -----------------------------
  const fullLockYaw = steerYawRate(vForward, 1, 0);
  out.steer =
    Math.abs(fullLockYaw) > 1e-6
      ? axis(angle / TURN_TIME / fullLockYaw)
      : angle > 0
        ? 1
        : -1;

  // --- speed ---------------------------------------------------------------
  const cornerSpeed = maxSpeedForRadius(requiredRadius);
  const arriveSpeed = options.stopAtTarget
    ? Math.sqrt(2 * ARRIVE_DECEL * Math.max(0, planar - tolerance))
    : Number.POSITIVE_INFINITY;
  const desiredSpeed = Math.min(options.targetSpeed ?? CAR_MAX_SPEED, cornerSpeed, arriveSpeed);

  const speedError = desiredSpeed - vForward;
  out.throttle = axis(speedError / SPEED_BAND) * throttleCap;

  // --- powerslide ----------------------------------------------------------
  out.handbrake =
    (options.allowPowerslide ?? false) && Math.abs(angle) > SLIDE_ANGLE && speed > SLIDE_MIN_SPEED;

  // --- boost ---------------------------------------------------------------
  out.boost =
    (options.allowBoost ?? false) &&
    !out.handbrake &&
    Math.abs(angle) < BOOST_ALIGN &&
    speedError > BOOST_SPEED_MARGIN &&
    car.boostAmount > (options.boostReserve ?? 0);

  return sanitizeInput(out);
};

/**
 * Tight recovery: full lock with the handbrake down, which trades the lateral
 * tyre friction (0.1x, see ../physics/ground) for rotation. Used when the car
 * is facing badly wrong and speed is not the problem.
 */
export const powerslide = (car: CarState, target: Vec3): CarInput => {
  const local = mulVecT(car.orientation, sub(target, car.position));
  const angle = Math.atan2(local.y, local.x);
  const out = neutralInput();
  out.throttle = 1;
  out.steer = angle > 0 ? 1 : -1;
  out.handbrake = true;
  return sanitizeInput(out);
};

// ---------------------------------------------------------------------------
// Shooting
// ---------------------------------------------------------------------------

/**
 * Where the car has to be to send the ball at `aimAt`.
 *
 * Directly opposite the aim direction, one ball radius plus half a car length
 * back — the car's ORIGIN, not its bumper, because that is what the drive
 * controller steers. `aimError` rotates the aim direction about the vertical,
 * which is the whole of the difficulty aim model: a steady per-shot bias, not
 * per-tick jitter, because jitter averages out over the approach and a bias
 * does not.
 */
export const shotContactPoint = (
  ballPosition: Vec3,
  aimAt: Vec3,
  aimError = 0,
  out: Vec3 = vec3(),
  config: CarConfig = OCTANE_CONFIG
): Vec3 => {
  const HIT_OFFSET = hitOffsetFor(config);
  const dx = aimAt.x - ballPosition.x;
  const dy = aimAt.y - ballPosition.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) {
    out.x = ballPosition.x - HIT_OFFSET;
    out.y = ballPosition.y;
    out.z = ballPosition.z;
    return out;
  }

  const c = Math.cos(aimError);
  const s = Math.sin(aimError);
  const ux = (dx * c - dy * s) / len;
  const uy = (dx * s + dy * c) / len;

  out.x = ballPosition.x - ux * HIT_OFFSET;
  out.y = ballPosition.y - uy * HIT_OFFSET;
  out.z = ballPosition.z;
  return out;
};

export interface ShotOptions extends DriveOptions {
  /** Radians of aim bias, drawn by the caller from its seeded generator. */
  aimError?: number;
  /** Flip into the ball on the last stretch. */
  allowFlip?: boolean;
}

/** Distance at which a forward flip lands on the ball rather than short of it.
 *  A flip covers roughly 500 uu/s * 0.65 s of torque time, but the useful
 *  window is the last third of that. */
const FLIP_RANGE = 420;
/** Do not flip below this speed: the flip's flat +500 uu/s is worth less than
 *  the half-second of control it costs. */
const FLIP_MIN_SPEED = 900;

/**
 * Drive at the ball so that the contact sends it toward `aimAt`.
 *
 * The car is steered at the contact point rather than at the ball, which is the
 * entire difference between a bot that pushes the ball around and one that
 * shoots. The ball's own height is ignored: this is the GROUND mechanic, and
 * the decision layer is responsible for not calling it on a ball in the air.
 */
export const groundShot = (
  car: CarState,
  ballPosition: Vec3,
  aimAt: Vec3,
  options: ShotOptions = {}
): CarInput => {
  const contact = shotContactPoint(ballPosition, aimAt, options.aimError ?? 0);
  contact.z = car.position.z;

  const input = driveToPoint(car, contact, options);

  if (options.allowFlip === true && car.onGround && car.hasFlip) {
    const distance = Math.hypot(
      contact.x - car.position.x,
      contact.y - car.position.y
    );
    const vForward = dot(car.velocity, forward(car.orientation));
    const local = mulVecT(car.orientation, sub(ballPosition, car.position));
    const aligned = Math.abs(Math.atan2(local.y, local.x)) < 0.15;
    if (distance < FLIP_RANGE && vForward > FLIP_MIN_SPEED && aligned) {
      input.jump = true;
      input.pitch = -1; // forward flip; see the dodge-sign note in the header
      input.boost = false;
    }
  }

  return sanitizeInput(input);
};

// ---------------------------------------------------------------------------
// Aerials
// ---------------------------------------------------------------------------

/**
 * PD gains for pointing the nose at a target in the air.
 *
 * The plant is a double integrator — input sets angular ACCELERATION through
 * `AERIAL_TORQUE`, not angular velocity — so the damping term is what makes it
 * settle instead of oscillate. The signs are derived, not guessed: writing the
 * direction-to-target `d` in the car's local frame and differentiating gives
 *     d(d_z)/dt = +omega_y * d_x     and     d(d_y)/dt = -omega_z * d_x
 * and `AERIAL_TORQUE.pitch` is NEGATIVE while `.yaw` is positive, which is
 * exactly why the pitch damping term adds and the yaw damping term subtracts.
 */
const AERIAL_P = 2.2;
const AERIAL_D = 0.36;

/**
 * Vertical distance from the car's ORIGIN to the centre of a ball its roof is
 * touching: the top of the hitbox plus one ball radius.
 *
 * Body-dependent for the same reason `hitOffsetFor` is — a Dominus roof sits
 * 7.4 uu lower than an Octane's, so a bot driving one and planning against the
 * Octane number stops that far short in the air, where there is no second
 * chance. Defaults to Octane so existing callers are unchanged.
 */
export const verticalReachFor = (config: CarConfig = OCTANE_CONFIG): number =>
  config.hitboxOffset.z + config.hitboxSize.z * 0.5 + BALL_RADIUS;

/**
 * The vertical half of a takeoff from rest, integrated once at module load.
 *
 * This is what a jump is WORTH, and it is the number the reach bounds in
 * ./intents are built from. Boost is deliberately absent: these are the
 * heights an empty car reaches, which is the only bound that holds when the
 * tank is dry.
 */
export interface TakeoffProfile {
  /** Highest the car's origin gets, in uu above the floor. */
  readonly apex: number;
  /** Seconds from the press to the apex. */
  readonly apexTime: number;
}

/**
 * Integrate the vertical takeoff at the simulation's own tick.
 *
 * NOT THE CLOSED FORM, and the gap is not rounding. Integrating the hold
 * analytically and finishing with `v^2 / 2g` gives 237.5 uu for a full-hold
 * jump where the simulator flies 222.75 — 6.6 % high, and the same error is
 * 20.8 uu on the double jump, which is most of the margin an aerial has. It
 * splits into two named causes, and this function reproduces both:
 *
 *   - THE STICKY FORCE, 13.6 uu of it. For the first six ticks the wheels are
 *     still down, and a grounded car is pulled into its surface at
 *     STICKY_FORCE_GROUND_ACCEL — half a gravity — on top of gravity itself.
 *     Contact ends when the FIRST pair of wheels runs out of suspension reach,
 *     because three wheels are needed for contact and losing a pair leaves two:
 *     `detachHeight` below, 30.5 uu for an Octane.
 *   - THE 120 Hz INTEGRATION, the remaining 1.2 uu. Semi-implicit Euler, the
 *     same bias ../sim/world has. Same trade ./intents makes for the throttle
 *     curve, and for the same reason: a reach bound that over-claims is a bot
 *     that commits to a ball it will pass underneath.
 *
 * What is left is the vertical component of ../physics/jump and nothing else:
 * the impulse, the JUMP_PRE_MIN_ACCEL_SCALE window, and the hold force dying at
 * JUMP_MAX_HOLD_TIME whether the button is still down or not. The result agrees
 * with a flown car to 0.2 uu — pinned in test/bot/mechanics.test.ts.
 *
 * THE RELEASE TICK IS REAL. `secondJump` spends the double jump on the second
 * tick after the hold ends, not the first: the jump button is edge-detected, so
 * it has to come up for one tick before the next press counts at all. That tick
 * costs DOUBLE_JUMP_IMPULSE * DT = 2.43 uu of apex and there is no way to buy
 * it back — releasing a tick earlier instead ends the hold a tick early, which
 * costs five times as much.
 */
const buildTakeoff = (
  secondJump: boolean,
  dt: number,
  config: CarConfig = OCTANE_CONFIG
): TakeoffProfile => {
  let ticks = 0;
  let v = JUMP_IMPULSE;
  let z = CAR_SPAWN_REST_Z;

  // Origin height at which a wheel pair's ray (restLength + radius, cast down
  // from its hardpoint) stops reaching the floor.
  const detachHeight = Math.min(
    config.frontWheels.suspensionRestLength +
      config.frontWheels.radius -
      config.frontWheels.connectionOffset.z,
    config.backWheels.suspensionRestLength +
      config.backWheels.radius -
      config.backWheels.connectionOffset.z
  );

  const tick = (): void => {
    // Read BEFORE the move, the way the suspension pass does.
    if (z <= detachHeight) v -= STICKY_FORCE_GROUND_ACCEL * dt;
    v -= GRAVITY * dt;
    z += v * dt;
    ticks++;
  };

  for (let held = 0; held < JUMP_MAX_HOLD_TIME - 1e-9; held += dt) {
    const scale = held < JUMP_MIN_HOLD_TIME - 1e-9 ? JUMP_PRE_MIN_ACCEL_SCALE : 1;
    v += JUMP_HOLD_ACCELERATION * scale * dt;
    tick();
  }

  if (secondJump) {
    tick(); // the release tick — see the header
    v += DOUBLE_JUMP_IMPULSE;
  }

  let apex = z;
  for (;;) {
    v -= GRAVITY * dt;
    z += v * dt;
    if (z <= apex) break;
    apex = z;
    ticks++;
  }

  return { apex, apexTime: ticks * dt };
};

/** A full-hold jump, and the same jump with the second one spent as early as
 *  the button edge allows. The two apexes are the whole of what this change is
 *  about; `test/physics/jump.test.ts` pins both against the simulator. */
export const TAKEOFF_SINGLE: TakeoffProfile = buildTakeoff(false, DT);
export const TAKEOFF_DOUBLE: TakeoffProfile = buildTakeoff(true, DT);

/**
 * Height slack, uu, on the second-jump decision.
 *
 * `shouldSpendSecondJump` fires on the LAST tick that still makes the height,
 * so with no slack the car arrives with exactly zero to spare and any of — a
 * tick of reaction lag, the tick spent releasing the button, a few degrees of
 * tilt — turns "just reaches" into "just misses". 30 uu is a dozen ticks of the
 * 2.43 uu/tick decay derived below, and erring early only costs altitude the
 * car did not need.
 */
const DOUBLE_JUMP_HEIGHT_MARGIN = 30;

/** Horizontal offset, uu, below which there is nothing left to point at and the
 *  ground turn is skipped. One car length. */
const AERIAL_TURN_MIN_PLANAR = 120;

/**
 * Is this the tick to spend the second jump?
 *
 * THE WHOLE DECISION IS ONE IDENTITY, and it is exact. The second jump is a
 * STEP in vertical velocity, so its contribution to the car's height at any
 * later time T is DOUBLE_JUMP_IMPULSE * (T - d), linear in how long it is given
 * to act. Delaying it by `d` therefore costs exactly DOUBLE_JUMP_IMPULSE * d of
 * height at T — and, taking T at the apex, drops the apex itself by exactly
 * DOUBLE_JUMP_IMPULSE per second of delay, which is 2.43 uu per tick. Nothing
 * about that is an approximation: the measured apexes are 474.5 uu at the
 * earliest legal moment and 340.8 uu 55 ticks later, and the difference is
 * 133.7 against a predicted 133.68. `test/physics/jump.test.ts` pins it.
 *
 * So the rule is: FIRE ON THE LAST TICK THAT STILL MAKES THE HEIGHT. Height is
 * monotonically lost by waiting, so there is never a reason to fire earlier
 * than that — and there is a reason not to, because every tick before the jump
 * is a tick of air control spent pointing the nose at the target, and because
 * the ballistic estimate below ignores boost still in the tank and so is a
 * FLOOR on where the car can get to.
 *
 * `upZ` is why waiting is not free either: the impulse is applied along the
 * car's own up axis (RocketSim's `GetUpDir() * JUMP_IMMEDIATE_FORCE`), so a car
 * that has already pitched over toward its target collects only that fraction
 * of it, and a car past 90 degrees collects none. Rotating therefore makes this
 * test fire sooner, on its own, without a special case.
 *
 * `arrivalTime` is what turns the apex rule into a real interception, and the
 * difference is not cosmetic. The question is not "can the car ever get that
 * high" but "can it be that high WHEN THE BALL IS THERE", and against a ball
 * 200 uu above the single-jump apex the two answers are 60 ticks apart: asking
 * about the apex says yes for half a second after it has stopped being true,
 * because the car cannot HOLD its apex — it falls back through the height it
 * was asked about. Evaluating the parabola at the deadline instead is what
 * makes the bot fire on the way up rather than on the way down. Omitted, the
 * deadline defaults to the apex and this is a pure height test again.
 */
export const shouldSpendSecondJump = (
  car: CarState,
  neededHeight: number,
  arrivalTime?: number
): boolean => {
  const vz = car.velocity.z + DOUBLE_JUMP_IMPULSE * upOf(car.orientation).z;
  // Inverted, or already falling hard enough that the impulse does not even
  // turn the car around: there is no height on offer, so keep the flip.
  if (vz <= 0) return false;

  // With no deadline the question collapses to "how high can this get at all",
  // and the answer is the apex — which is what `vz / GRAVITY` seconds gets to.
  const t = arrivalTime ?? vz / GRAVITY;
  const height = car.position.z + vz * t - 0.5 * GRAVITY * t * t;
  return height <= neededHeight + DOUBLE_JUMP_HEIGHT_MARGIN;
};

/**
 * Fly at a point.
 *
 * FOUR PHASES, all of them recovered from the car's own state rather than from
 * a counter, because a mechanic here owns no memory (see the file header) and
 * because a counter would desynchronise from the physics the first time the car
 * was bumped:
 *
 *   1. ON THE GROUND. Turn until pointed at the target, then jump.
 *   2. THE HOLD. `hasJumped && airTime === 0` is ../physics/jump's own
 *      invariant for "the first jump is still burning". Hold the button for the
 *      whole window and stay FLAT: both the hold force and the second jump act
 *      along the car's up axis, so a degree of tilt spent here is paid for
 *      twice. This alone is worth 140 uu — a released jump peaks at 83 uu and a
 *      held one at 223.
 *   3. THE SECOND JUMP. `shouldSpendSecondJump` above. Worth another 250 uu.
 *   4. FLIGHT. Point the nose at the target and boost, which is what this
 *      mechanic used to be in its entirety.
 *
 * The PD law in phase 4 is unchanged and is what keeps the car from spinning;
 * the damping term is why it settles rather than oscillates.
 *
 * WHAT PHASE 1 DELIBERATELY DOES NOT DO is delay the takeoff until the climb
 * fits in the time left, which is the obvious companion to the second-jump
 * solve and which was measured and rejected. Gating the jump on
 * `arrivalTime <= climbTime` halved the All-Star reference's scoring on the RL
 * random-reset distribution — 6 goals in 40 episodes down to 3 — because
 * `reach.time` is the time to DRIVE to the interception and says nothing about
 * whether the car is under it yet, so the gate mostly held the car on the floor
 * while the ball ran away. The second jump does not have that problem: by the
 * time it is being decided the car is already committed and airborne, and the
 * only question left is a vertical one.
 */
export interface AerialOptions {
  allowBoost?: boolean;
  boostReserve?: number;
  /** Spend the second jump on the way up. Gated per tier — see ./difficulty. */
  allowDoubleJump?: boolean;
  /** Seconds until the target is at `target`. Without it the second jump is
   *  spent for maximum apex rather than for a height at a moment — see
   *  `shouldSpendSecondJump`. */
  arrivalTime?: number;
}

export const aerialHit = (car: CarState, target: Vec3, options: AerialOptions = {}): CarInput => {
  const toTarget = sub(target, car.position);
  const local = mulVecT(car.orientation, toTarget);
  const doubleJump = options.allowDoubleJump ?? false;

  // Height the car's ORIGIN has to reach for its roof to meet the ball centre.
  const needed = target.z - verticalReachFor(car.config);

  // Phase 2 FIRST, and before the ground branch, because a car is still
  // grounded for the first six ticks of its own jump. One tick of the button
  // coming up in there does two kinds of damage: it ends the hold early, and it
  // hands the NEXT press an edge — which, `airTime` being zero for as long as
  // the hold runs, spends the second jump at 50 uu instead of at 90.
  if (car.hasJumped && car.airTime === 0) {
    const out = neutralInput();
    out.throttle = 1;
    out.jump = true;
    out.boost =
      (options.allowBoost ?? true) &&
      car.boostAmount > (options.boostReserve ?? 0) &&
      normalize(local).x > 0.85;
    return sanitizeInput(out);
  }

  if (car.onGround) {
    // Horizontal offset only. A target directly overhead has no heading — both
    // `local.x` and `local.y` are rounding error and the atan2 of them is a
    // coin flip, which had the car turning away from a ball it was already
    // underneath. Below one car length there is nothing to turn toward.
    const planar = Math.hypot(toTarget.x, toTarget.y);
    const heading = Math.atan2(local.y, local.x);
    if (planar > AERIAL_TURN_MIN_PLANAR && Math.abs(heading) > 0.25) {
      // Not pointed at it yet — turn on the ground, which is free.
      return driveToPoint(car, vec3(target.x, target.y, car.position.z), {
        allowBoost: options.allowBoost ?? false,
        boostReserve: options.boostReserve ?? 0,
      });
    }

    const out = neutralInput();
    out.throttle = 1;
    out.jump = true;
    out.boost = (options.allowBoost ?? true) && car.boostAmount > (options.boostReserve ?? 0);
    return sanitizeInput(out);
  }

  const d = normalize(local);
  const boosting =
    (options.allowBoost ?? true) && car.boostAmount > (options.boostReserve ?? 0) && d.x > 0.85;

  const w = mulVecT(car.orientation, car.angularVelocity);
  const u = mulVecT(car.orientation, vec3(0, 0, 1));

  const out = neutralInput();
  out.pitch = axis(AERIAL_P * d.z + AERIAL_D * w.y);
  out.yaw = axis(AERIAL_P * d.y - AERIAL_D * w.z);
  out.roll = axis(AERIAL_P * u.y + AERIAL_D * w.x);
  out.boost = boosting;

  // Phase 3 — the second jump. `hasFlip` is the single shared resource: it is
  // still set for a car that drove off a ledge without jumping at all, which is
  // correct, and it is already spent for one that has dodged.
  if (
    doubleJump &&
    car.hasFlip &&
    !car.isDodging &&
    shouldSpendSecondJump(car, needed, options.arrivalTime)
  ) {
    if (wasJumpDown(car)) {
      // The button is still down, so a press this tick is not an EDGE and does
      // nothing at all. Release it; the press lands next tick and costs the
      // 2.43 uu that one tick of delay is worth.
      out.jump = false;
    } else {
      out.jump = true;
      // A jump pressed in the air with |pitch| + |yaw| + |roll| at or above the
      // body's dodge deadzone is a FLIP, and a flip spends this very resource
      // for no height whatsoever — it is the one way to turn the mechanic into
      // its own opposite. Every rotation axis is zeroed for this single tick.
      out.pitch = 0;
      out.yaw = 0;
      out.roll = 0;
    }
  }

  return sanitizeInput(out);
};

// ---------------------------------------------------------------------------
// Wall reads
// ---------------------------------------------------------------------------

/** How close to a wall plane a predicted sample counts as a wall bounce. */
const WALL_MARGIN = 260;
/** Below this height the "wall" read is really just a ground ball. */
const WALL_MIN_Z = 300;

/**
 * Meet a ball that is going to come off a wall.
 *
 * Two-stage, because a car cannot drive at a point on a wall from the middle of
 * the field: first drive to the floor point directly beneath the predicted wall
 * contact, then — once close and moving — drive at the contact itself, which
 * the suspension turns into a wall climb on its own.
 *
 * Returns a plain drive at the ball's ground projection when no wall contact is
 * predicted, so the caller never has to branch on null.
 */
export const wallRead = (
  car: CarState,
  prediction: BallPrediction,
  options: DriveOptions = {}
): CarInput => {
  const sample = makeSample();
  const step = 4;

  for (let t = 0; t < prediction.ticks; t += step) {
    prediction.readInto(t, sample);
    const p = sample.position;
    if (p.z < WALL_MIN_Z) continue;

    const nearSide = 4096 - Math.abs(p.x) < WALL_MARGIN;
    const nearBack = 5120 - Math.abs(p.y) < WALL_MARGIN;
    if (!nearSide && !nearBack) continue;

    // The floor point below the contact, pulled slightly in off the wall so the
    // car approaches the fillet rather than the flat.
    const approach = vec3(p.x * 0.92, p.y * 0.92, car.position.z);
    const toApproach = Math.hypot(approach.x - car.position.x, approach.y - car.position.y);
    const closing = dot(car.velocity, normalize(sub(approach, car.position)));

    if (toApproach < 600 && closing > 400) {
      return driveToPoint(car, vec3(p.x, p.y, p.z), options);
    }
    return driveToPoint(car, approach, options);
  }

  prediction.readInto(0, sample);
  return driveToPoint(
    car,
    vec3(sample.position.x, sample.position.y, car.position.z),
    options
  );
};

// ---------------------------------------------------------------------------
// Kickoffs
// ---------------------------------------------------------------------------

export interface KickoffOptions extends DriveOptions {
  /** Ticks since the kickoff went live. Drives every timing below. */
  elapsedTicks: number;
}

/** Ticks into the run at which the speed-flip is started. Early enough that the
 *  car is still accelerating (so the flip's flat +500 uu/s is not wasted
 *  against the speed cap) and late enough to be pointed at the ball. */
const SPEED_FLIP_START = 14;
/** Ticks the jump is held before the diagonal dodge. */
const SPEED_FLIP_JUMP_TICKS = 3;
/** Ticks the cancel pitch is held after the dodge. */
const SPEED_FLIP_CANCEL_TICKS = 20;

/**
 * The plain kickoff: drive at the ball with boost and flip into it at the end.
 * Works from all five spawns because the target is the ball, not a memorised
 * route — the diagonal spawns naturally arrive at an angle.
 */
export const kickoffFastDiagonal = (
  car: CarState,
  ballPosition: Vec3,
  options: KickoffOptions
): CarInput => {
  const distance = Math.hypot(
    ballPosition.x - car.position.x,
    ballPosition.y - car.position.y
  );

  const input = driveToPoint(car, vec3(ballPosition.x, ballPosition.y, car.position.z), {
    ...options,
    allowBoost: options.allowBoost ?? true,
    allowReverse: false,
    boostReserve: 0,
  });
  input.throttle = clamp(options.throttleCap ?? 1, 0, 1);

  const vForward = dot(car.velocity, forward(car.orientation));
  if (car.onGround && car.hasFlip && distance < FLIP_RANGE + BALL_RADIUS && vForward > FLIP_MIN_SPEED) {
    input.jump = true;
    input.pitch = -1;
    input.boost = false;
  }
  return sanitizeInput(input);
};

/**
 * Speed flip: a diagonal front flip cancelled immediately, which preserves the
 * momentum a completed flip would scrub (docs/FEEL_TARGETS.md — the speed comes
 * from the CANCEL, not from the impulse, which is a flat 500 uu/s either way).
 *
 * The sequence is driven off `elapsedTicks` alone, so it is reproducible and
 * has no hidden state: jump, hold three ticks, dodge diagonally
 * (pitch -1 for forward, yaw toward the ball side), then hold pitch +1 —
 * the same sign as `dodgeDirection.x` — which is what cancels it.
 */
export const kickoffSpeedFlip = (
  car: CarState,
  ballPosition: Vec3,
  options: KickoffOptions
): CarInput => {
  const t = options.elapsedTicks;
  const input = kickoffFastDiagonal(car, ballPosition, options);

  if (t < SPEED_FLIP_START) return input;

  const phase = t - SPEED_FLIP_START;
  if (phase < SPEED_FLIP_JUMP_TICKS) {
    input.jump = true;
    return sanitizeInput(input);
  }
  if (phase === SPEED_FLIP_JUMP_TICKS) {
    // Release for one tick so the next press is a fresh edge.
    input.jump = false;
    return sanitizeInput(input);
  }
  if (phase === SPEED_FLIP_JUMP_TICKS + 1) {
    input.jump = true;
    input.pitch = -1;
    // Dodge toward the side the car is already angled to, which keeps the flip
    // pointing at the ball from the corner spawns.
    input.yaw = car.position.x > 0 ? 1 : -1;
    return sanitizeInput(input);
  }
  if (phase <= SPEED_FLIP_JUMP_TICKS + 1 + SPEED_FLIP_CANCEL_TICKS) {
    input.jump = false;
    input.pitch = 1; // same sign as dodgeDirection.x -> cancels the flip
    return sanitizeInput(input);
  }
  return input;
};

/**
 * Cheat back: do not contest the kickoff at all. Take the diagonal boost on the
 * way to a defensive position, which is what the second and third players do.
 */
export const kickoffCheatBack = (
  car: CarState,
  team: 0 | 1,
  options: KickoffOptions
): CarInput => {
  const side = car.position.x >= 0 ? 1 : -1;
  const y = team === 0 ? -2000 : 2000;
  const target = vec3(side * 1800, y, car.position.z);
  return driveToPoint(car, target, {
    ...options,
    allowBoost: false,
    stopAtTarget: true,
    arriveTolerance: 250,
  });
};

/** Which of the three kickoff variants a car should run. Exported so the
 *  decision layer's choice is inspectable in a test. */
export type KickoffVariant = 'fast-diagonal' | 'speed-flip' | 'cheat-back';

export const runKickoff = (
  variant: KickoffVariant,
  car: CarState,
  team: 0 | 1,
  ballPosition: Vec3,
  options: KickoffOptions
): CarInput => {
  switch (variant) {
    case 'fast-diagonal':
      return kickoffFastDiagonal(car, ballPosition, options);
    case 'speed-flip':
      return kickoffSpeedFlip(car, ballPosition, options);
    case 'cheat-back':
      return kickoffCheatBack(car, team, options);
  }
};

/** True when the car's up axis has fallen away from the surface it is on —
 *  i.e. it is upside down or on its side and needs a recovery, not a plan. */
export const needsRecovery = (car: CarState): boolean =>
  !car.onGround && dot(upOf(car.orientation), vec3(0, 0, 1)) < 0.3;

/**
 * True when the car is driving on something that is not the floor.
 *
 * `position.z` means two different things on the two sides of this predicate. On
 * the floor it is a ride height and carrying it onto a target is a harmless
 * normalisation. On a wall it is HOW FAR UP THE WALL THE CAR IS, so lifting a
 * floor target to the car's own z tells `driveToPoint` to hold that height — and
 * it does, indefinitely, because holding it is a perfectly achievable request. A
 * car that had driven into the back of its own net sat there at 8 uu/s under
 * full throttle for twelve seconds on exactly that instruction.
 *
 * `'ramp'` is included: the fillet at the foot of a wall is where a car that has
 * come down one actually ends up, and it is still not a floor.
 */
export const onNonFloorSurface = (car: CarState): boolean =>
  car.onGround &&
  (car.surface === 'wall' || car.surface === 'ceiling' || car.surface === 'ramp');

// ---------------------------------------------------------------------------
// Wedges
// ---------------------------------------------------------------------------

/**
 * Speed, uu/s, below which a car that has been told to drive is not driving.
 * Three ticks of throttle from a standstill clears it, so a car that is merely
 * pulling away is never mistaken for a wedged one.
 */
export const WEDGE_SPEED = 60;
/** |throttle| above which the bot counts as having ASKED for motion. */
export const WEDGE_THROTTLE = 0.1;
/** Ticks of asking without moving before the car is treated as wedged. */
export const WEDGE_TICKS = 60;
/** Ticks `unstick` is held once it fires. Long enough to clear the geometry
 *  that caused the wedge, short enough not to be a manoeuvre in its own right. */
export const UNSTICK_TICKS = 45;

/**
 * Back a wedged car out.
 *
 * A car pinned nose-first into arena geometry — the inside corner of its own
 * net is the one that actually happens — cannot drive out of it, and cannot
 * turn out of it either: the yaw rate is `curvature(v) * v * steer`, so at 8
 * uu/s even full lock buys 0.06 rad/s and the steering is effectively dead. The
 * only control left that moves the car at all is reverse, and `driveToPoint`
 * will not choose it, because its reverse branch is capped at
 * REVERSE_MAX_DISTANCE and a wedged car's target is usually much further away
 * than that. So this is a separate mechanic rather than a tweak to that cap:
 * reversing across the arena is still wrong, and reversing out of a wall is
 * still right.
 *
 * The steer sign flips with the direction of travel for the same reason it does
 * in `driveToPoint`'s reverse branch — the yaw rate carries the sign of the
 * forward speed, so steering "toward" the target while rolling backwards turns
 * the nose away from it.
 */
export const unstick = (car: CarState, target: Vec3): CarInput => {
  const local = mulVecT(car.orientation, sub(target, car.position));
  const towardTarget = Math.atan2(local.y, local.x) > 0 ? 1 : -1;
  const out = neutralInput();
  out.throttle = -1;
  out.steer = dot(car.velocity, forward(car.orientation)) < 0 ? -towardTarget : towardTarget;
  // A car with no wheels on anything gets NOTHING from the throttle: the engine
  // force is applied through the wheels, and a car resting on its roof or
  // balanced on a goal post has none of them down. Boost is the one thrust in
  // the game that does not go through the wheels, so it is the only control
  // such a car has, and it is spent only there — a genuinely airborne car is
  // never slow enough for long enough to be called wedged, so this cannot fire
  // in the middle of an aerial.
  out.boost = !car.onGround && car.boostAmount > 0;
  // On the ground, jump. Reverse alone gets a car out of most wedges and out of
  // some of them not at all — nose-first into the inside corner of a goal, the
  // engine is pushing against geometry in every direction the wheels can point.
  // A jump is the one input that leaves the surface entirely, and leaving the
  // surface is what ends a wedge; it is what a player does, too. Held rather
  // than pulsed, so it is one jump per escape and never a dodge.
  out.jump = car.onGround;
  return sanitizeInput(out);
};

/**
 * `u.z` at or below which the car is upside down rather than merely tilted.
 * -0.85 is 148 degrees of roll; nothing shallower needs the escape below.
 */
const INVERTED_UP_Z = -0.85;
/**
 * Magnitude of (u.x, u.y) below which the two proportional terms carry no
 * information about WHICH WAY to rotate — they are both about to be zero.
 */
const INVERTED_LEVEL = 0.15;

/**
 * Roll and pitch back to level while airborne.
 *
 * EXACTLY INVERTED IS A STATIONARY POINT of the law below, and it has to be
 * handled explicitly. Both proportional terms are components of world +Z
 * expressed in the car's frame: at u = (0, 0, -1) they are both exactly zero, so
 * a perfectly upside-down car is handed zero torque and stays perfectly upside
 * down. That is not a rounding problem that damping eventually shakes loose —
 * it is a saddle, and a car resting on its roof has nothing to shake it. One sat
 * there for twenty-two seconds of a 2v2 with the throttle pinned.
 *
 * The escape is a full roll, with a FIXED sign: the recovery has to stay
 * deterministic, and either direction is equally correct from a saddle.
 */
export const recover = (car: CarState): CarInput => {
  const w = mulVecT(car.orientation, car.angularVelocity);
  const u = mulVecT(car.orientation, vec3(0, 0, 1));
  const out = neutralInput();
  out.roll = axis(AERIAL_P * u.y + AERIAL_D * w.x);
  out.pitch = axis(-AERIAL_P * u.x + AERIAL_D * w.y);
  if (u.z <= INVERTED_UP_Z && Math.hypot(u.x, u.y) < INVERTED_LEVEL) out.roll = 1;
  out.throttle = 1;
  return sanitizeInput(out);
};
